import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} track - Scrollable scroll-snap track
 * @property {HTMLButtonElement} [previous] - Previous button
 * @property {HTMLButtonElement} [next] - Next button
 * @property {HTMLButtonElement[]} [dots] - Pagination dots, one per item (`data-index`)
 */

/**
 * Lightweight carousel on top of a native scroll-snap track.
 * Swipe and keyboard scrolling stay native; the buttons scroll by one item and are
 * disabled at both ends; optional dots show and set the current item.
 * `data-start="<index>"` on the element opens the track on that item (no animation).
 * `data-scrollable` is set while the track overflows, so CSS can show the controls.
 *
 * @extends {Component<Refs>}
 */
class AbsolutCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number | undefined} */
  #frame;

  #started = false;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  /** Scrolls to the previous item. */
  previous() {
    this.#scrollByItem(-1);
  }

  /** Scrolls to the next item. */
  next() {
    this.#scrollByItem(1);
  }

  /**
   * Scrolls to the item of the clicked dot.
   * @param {Event} event - Click event on a dot carrying `data-index`
   */
  goTo(event) {
    if (!(event.target instanceof Element)) return;

    const dot = event.target.closest('[data-index]');
    if (!(dot instanceof HTMLElement)) return;
    this.scrollToIndex(Number(dot.dataset.index));
  }

  /**
   * Scrolls the track so the item at `index` is at the start.
   * @param {number} index - Item index
   * @param {ScrollBehavior} [behavior] - Scroll behavior
   */
  scrollToIndex(index, behavior = 'smooth') {
    const { track } = this.refs;
    const item = track.children[index];
    if (!(item instanceof HTMLElement)) return;

    const padding = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    const delta = item.getBoundingClientRect().left - track.getBoundingClientRect().left - padding;
    track.scrollTo({ left: track.scrollLeft + delta, behavior });
  }

  #listen() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.refs.track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    this.#update();

    if (!this.#started && this.dataset.start) {
      this.#started = true;
      requestAnimationFrame(() => {
        if (this.hasAttribute('data-scrollable')) this.scrollToIndex(Number(this.dataset.start), 'instant');
        this.#update();
      });
    }
  }

  /** @param {number} direction */
  #scrollByItem(direction) {
    const { track } = this.refs;
    const item = track.firstElementChild;
    if (!(item instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = item.getBoundingClientRect().width + gap;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: direction * step * rtl });
  }

  #scheduleUpdate = () => {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const { track, previous, next, dots } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const scrollable = max > 1;

    this.toggleAttribute('data-scrollable', scrollable);
    if (previous) previous.disabled = !scrollable || position <= 1;
    if (next) next.disabled = !scrollable || position >= max - 1;

    if (!dots?.length) return;

    // Current item = the one whose start edge is closest to the track's start edge
    const padding = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    const origin = track.getBoundingClientRect().left + padding;
    let current = 0;
    let best = Infinity;
    [...track.children].forEach((item, index) => {
      const distance = Math.abs(item.getBoundingClientRect().left - origin);
      if (distance < best) {
        best = distance;
        current = index;
      }
    });
    if (position >= max - 1 && scrollable) current = track.children.length - 1;

    dots.forEach((dot, index) => {
      if (index === current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('absolut-carousel')) {
  customElements.define('absolut-carousel', AbsolutCarousel);
}
